<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue';
  import { useRoute, useRouter } from 'vue-router/composables';
  import { useIntervalFn, useTimeoutFn } from '@vueuse/core';
  import BillingService, {
    IBillingCheckoutSessionResponse,
  } from '@/api/services/billing.service';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';

  const ui = useUi();
  const route = useRoute();
  const router = useRouter();
  const billingService = new BillingService();

  const externalId = computed(() => {
    const q = route?.query?.external_id;
    if (typeof q === 'string') return q;
    if (Array.isArray(q) && typeof q[0] === 'string') return q[0];
    return null;
  });

  const session = ref<IBillingCheckoutSessionResponse | null>(null);
  const loading = ref(true);
  const finished = ref(false);
  const timedOut = ref(false);

  function safeDateLabel(iso: string | null) {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString();
  }

  type TBillingReturnUiState =
    | 'missing_reference'
    | 'loading'
    | 'confirmed'
    | 'failed'
    | 'expired'
    | 'timed_out';

  const uiState = computed<TBillingReturnUiState>(() => {
    if (!externalId.value) return 'missing_reference';
    if (session.value?.status === 'CONFIRMED') return 'confirmed';
    if (session.value?.status === 'FAILED') return 'failed';
    if (session.value?.status === 'EXPIRED') return 'expired';
    if (timedOut.value) return 'timed_out';
    return 'loading';
  });

  const nextBillingLabel = computed(() =>
    safeDateLabel(session.value?.nextBillingAt ?? null)
  );
  const displayStatusLabel = computed(() => {
    const raw = session.value?.subscriptionStatus;
    if (!raw) return null;

    const normalized = String(raw).trim().toUpperCase();
    if (!normalized || normalized === 'UNKNOWN') return null;

    // Provider-neutral statuses (from backend snapshot)
    if (normalized === 'ACTIVE') return 'Activa';
    if (normalized === 'PAST_DUE') return 'Pago pendiente';
    if (normalized === 'PAUSED') return 'Pausada';
    if (normalized === 'CANCELLED') return 'Cancelada';
    if (normalized === 'ENDED') return 'Finalizada';

    // Avoid showing raw/unknown provider values in UI.
    return null;
  });

  const stateMeta = computed(() => {
    switch (uiState.value) {
      case 'confirmed':
        return {
          tone: 'success' as const,
          icon: 'mdi-check-decagram',
          ctaText: 'Volver a Planes',
        };
      case 'failed':
        return {
          tone: 'error' as const,
          icon: 'mdi-close-circle-outline',
          ctaText: 'Volver a Planes',
        };
      case 'expired':
        return {
          tone: 'warning' as const,
          icon: 'mdi-clock-alert-outline',
          ctaText: 'Volver a Planes',
        };
      case 'timed_out':
        return {
          tone: 'warning' as const,
          icon: 'mdi-clock-outline',
          ctaText: 'Volver a Planes',
        };
      case 'missing_reference':
        return {
          tone: 'error' as const,
          icon: 'mdi-alert-circle-outline',
          ctaText: 'Ir a Planes',
        };
      default:
        return {
          tone: 'info' as const,
          icon: 'mdi-loading',
          ctaText: null,
        };
    }
  });

  const title = computed(() => {
    if (!externalId.value) return 'No se encontró la referencia del checkout';
    if (session.value?.status === 'CONFIRMED')
      return '¡Listo! Suscripción confirmada';
    if (session.value?.status === 'FAILED')
      return 'El pago no pudo confirmarse';
    if (session.value?.status === 'EXPIRED') return 'El checkout expiró';
    if (timedOut.value) return 'Todavía no pudimos confirmarlo';
    return 'Confirmando su suscripción…';
  });

  const subtitle = computed(() => {
    if (!externalId.value)
      return 'Vuelva a Configuración → Planes e intente nuevamente.';
    if (session.value?.status === 'CONFIRMED')
      return 'Lo estamos redirigiendo al panel del local.';
    if (session.value?.status === 'FAILED')
      return 'Puede intentar nuevamente desde Configuración → Planes.';
    if (session.value?.status === 'EXPIRED')
      return 'Puede iniciar el checkout nuevamente desde Configuración → Planes.';
    if (timedOut.value)
      return 'Esto puede demorar. Vuelva a Planes y revise el estado del checkout.';
    return 'Esto puede demorar unos segundos.';
  });

  async function pollOnce() {
    if (!externalId.value) {
      pause();
      loading.value = false;
      finished.value = true;
      return;
    }

    try {
      loading.value = true;
      session.value = await billingService.getCheckoutSession(externalId.value);

      if (session.value.status === 'CONFIRMED') {
        pause();
        finished.value = true;
        ui.showToast(AlertType.Success, 'Suscripción confirmada');

        const restaurantStringId = session.value.restaurantStringId;
        router.replace(
          restaurantStringId
            ? `/${restaurantStringId}/settings?tab=plan&paid=1`
            : '/dashboard'
        );
      }

      if (
        session.value.status === 'FAILED' ||
        session.value.status === 'EXPIRED'
      ) {
        pause();
        finished.value = true;
      }
    } catch (e) {
      ui.serverError(e, 'No se pudo confirmar el estado del checkout');
      pause();
      finished.value = true;
    } finally {
      loading.value = false;
    }
  }

  const { pause, resume } = useIntervalFn(pollOnce, 2000, { immediate: false });

  onMounted(() => {
    pollOnce();
    if (externalId.value) {
      resume();
    }
  });

  // Hard timeout so we don't poll indefinitely
  useTimeoutFn(() => {
    if (finished.value) return;
    pause();
    timedOut.value = true;
    finished.value = true;
  }, 90000);

  function goToPlans() {
    if (session.value?.restaurantStringId) {
      router.push(`/${session.value.restaurantStringId}/settings?tab=plan`);
      return;
    }
    router.push('/dashboard');
  }
</script>

<template>
  <v-container class="billing-return">
    <v-row justify="center" class="billing-return__row">
      <v-col cols="12" sm="10" md="7" lg="6">
        <v-card class="billing-return__card" elevation="0">
          <v-progress-linear
            v-if="loading && !finished"
            class="billing-return__progress"
            indeterminate
            height="3"
            color="orange"
          />

          <div class="billing-return__content">
            <div
              class="billing-return__icon"
              :class="`billing-return__icon--${stateMeta.tone}`"
            >
              <v-progress-circular
                v-if="loading && !finished"
                indeterminate
                size="44"
                width="3"
                color="orange"
              />
              <v-icon v-else size="44">
                {{ stateMeta.icon }}
              </v-icon>
            </div>

            <div class="billing-return__title">
              {{ title }}
            </div>
            <div class="billing-return__subtitle">
              {{ subtitle }}
            </div>

            <div v-if="externalId" class="billing-return__details">
              <div class="billing-return__detail">
                <div class="billing-return__detail-label">Referencia</div>
                <div class="billing-return__detail-value">
                  {{ externalId }}
                </div>
              </div>

              <div v-if="displayStatusLabel" class="billing-return__detail">
                <div class="billing-return__detail-label">Estado</div>
                <div class="billing-return__detail-value">
                  {{ displayStatusLabel }}
                </div>
              </div>

              <div v-if="nextBillingLabel" class="billing-return__detail">
                <div class="billing-return__detail-label">Próximo cobro</div>
                <div class="billing-return__detail-value">
                  {{ nextBillingLabel }}
                </div>
              </div>
            </div>

            <div v-if="finished" class="billing-return__actions">
              <v-btn class="billing-return__btn" large @click="goToPlans">
                <v-icon left>mdi-arrow-left</v-icon>
                {{ stateMeta.ctaText || 'Volver a Planes' }}
              </v-btn>

              <div class="billing-return__fineprint">
                <v-icon class="billing-return__fineprint-icon" size="16">
                  mdi-shield-check
                </v-icon>
                <span>Si ya pagó, su plan se activará automáticamente.</span>
              </div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped lang="scss">
  .billing-return {
    padding-top: 24px;
    padding-bottom: 24px;

    &__row {
      min-height: 70vh;
      align-items: center;
    }

    &__card {
      border-radius: 18px;
      overflow: hidden;
      border: 1px solid rgba(15, 23, 42, 0.08);
      box-shadow: 0 22px 48px rgba(15, 23, 42, 0.12);
      background: radial-gradient(
          1000px 500px at 0% 0%,
          rgba(255, 111, 0, 0.14),
          rgba(255, 255, 255, 0)
        ),
        #ffffff;
    }

    &__progress {
      opacity: 0.9;
    }

    &__content {
      padding: 26px 22px 22px;
      text-align: center;
    }

    &__icon {
      width: 76px;
      height: 76px;
      margin: 0 auto 14px;
      border-radius: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid rgba(15, 23, 42, 0.08);
      background: rgba(15, 23, 42, 0.03);

      &--success {
        background: rgba(34, 197, 94, 0.1);
        border-color: rgba(34, 197, 94, 0.18);
        color: rgba(20, 83, 45, 0.98);
        animation: billingReturnPop 520ms ease-out;
      }

      &--error {
        background: rgba(239, 68, 68, 0.1);
        border-color: rgba(239, 68, 68, 0.18);
        color: rgba(127, 29, 29, 0.98);
        animation: billingReturnPop 520ms ease-out;
      }

      &--warning {
        background: rgba(245, 158, 11, 0.12);
        border-color: rgba(245, 158, 11, 0.22);
        color: rgba(120, 53, 15, 0.98);
        animation: billingReturnPop 520ms ease-out;
      }

      &--info {
        background: rgba(59, 130, 246, 0.1);
        border-color: rgba(59, 130, 246, 0.18);
        color: rgba(30, 58, 138, 0.98);
      }
    }

    &__title {
      font-size: 20px;
      font-weight: 900;
      letter-spacing: -0.2px;
      color: rgba(15, 23, 42, 0.92);
      margin-bottom: 6px;
    }

    &__subtitle {
      font-size: 13px;
      color: rgba(15, 23, 42, 0.62);
      font-weight: 700;
      margin-bottom: 16px;
    }

    &__details {
      margin: 0 auto 18px;
      text-align: left;
      max-width: 520px;
      display: grid;
      gap: 10px;
      padding: 14px 14px;
      border-radius: 16px;
      border: 1px solid rgba(15, 23, 42, 0.08);
      background: rgba(255, 255, 255, 0.75);
    }

    &__detail-label {
      font-size: 12px;
      font-weight: 900;
      color: rgba(15, 23, 42, 0.56);
      letter-spacing: 0.2px;
      margin-bottom: 2px;
    }

    &__detail-value {
      font-size: 13px;
      font-weight: 900;
      color: rgba(15, 23, 42, 0.86);
      word-break: break-word;
    }

    &__actions {
      display: grid;
      justify-items: center;
      gap: 10px;
    }

    &__btn {
      border-radius: 14px;
      height: 50px;
      min-width: 220px;
      text-transform: none;
      font-weight: 900;
      letter-spacing: 0.2px;
      background: rgba(15, 23, 42, 0.92) !important;
      color: #fff !important;
      box-shadow: 0 18px 34px rgba(15, 23, 42, 0.18) !important;
    }

    &__fineprint {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      color: rgba(15, 23, 42, 0.56);
      font-weight: 700;
    }

    &__fineprint-icon {
      color: rgba(34, 197, 94, 0.9);
    }
  }

  @keyframes billingReturnPop {
    0% {
      transform: scale(0.96);
      opacity: 0.6;
    }
    70% {
      transform: scale(1.04);
      opacity: 1;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
</style>
